import { createContext, useContext } from 'react'
import { cx } from './cx.js'
import {
  exactEth,
  exactTokenAmount,
  exactUsd,
  formatEth,
  formatPct,
  formatTokenAmount,
  formatUsd,
  signum,
  type SignMode,
} from '../format/decimal.js'

/*
 * Every number that is money or a percentage renders through these. They
 * handle sign (U+2212 minus), precision, tabular figures, and expose the
 * exact stored value as a tooltip and to assistive tech.
 */

export interface MoneyProps {
  /** USD decimal string */
  value: string | null | undefined
  precision?: number
  compact?: boolean
  sign?: SignMode
  className?: string
}

export function Money({ value, precision = 2, compact, sign = 'auto', className }: MoneyProps) {
  if (value == null) return <span className={cx('num text-ink-3', className)}>—</span>
  const shown = formatUsd(value, { precision, compact: compact ?? false, sign })
  const exact = exactUsd(value)
  return (
    <span className={cx('num whitespace-nowrap', className)} title={exact === shown ? undefined : `${exact} USD`} data-exact={value}>
      {shown}
    </span>
  )
}

export interface PnlNumberProps extends MoneyProps {
  /** show ▲/▼ glyph (default true): colour is never the only signal */
  arrow?: boolean
  /** render a percent instead of USD */
  percent?: boolean
  /** `eth`: the value is ETH (the platform's P&L, lane ALGOBOO1), shown with 4 fraction digits unless `precision` says */
  unit?: 'usd' | 'eth'
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const sizeCls = { sm: 'text-[12.5px]', md: 'text-[14px]', lg: 'text-[18px]', xl: 'text-[30px] leading-none' }

export function PnlNumber({ value, precision, compact, arrow = true, percent, unit = 'usd', size = 'md', className, sign = 'always' }: PnlNumberProps) {
  const s = signum(value)
  const tone = s > 0 ? 'text-gain' : s < 0 ? 'text-loss' : 'text-ink-3'
  const text =
    value == null
      ? '—'
      : percent
        ? formatPct(value, { precision: precision ?? 2, sign })
        : unit === 'eth'
          ? formatEth(value, { precision: precision ?? 4, sign })
          : formatUsd(value, { precision: precision ?? 2, compact: compact ?? false, sign })
  const exact = value == null ? undefined : percent ? `${value}%` : unit === 'eth' ? exactEth(value) : `${exactUsd(value)} USD`
  const word = s > 0 ? 'gain' : s < 0 ? 'loss' : 'flat'
  return (
    <span
      className={cx('num inline-flex items-center gap-1 whitespace-nowrap font-semibold', tone, sizeCls[size], className)}
      title={exact}
      data-exact={value ?? undefined}
      data-sign={s}
    >
      {arrow && value != null && <PnlArrow s={s} size={size === 'xl' ? 16 : size === 'lg' ? 12 : 9} />}
      <span>{text}</span>
      <span className="sr-only">{` (${word})`}</span>
    </span>
  )
}

function PnlArrow({ s, size }: { s: -1 | 0 | 1; size: number }) {
  if (s === 0) return <svg aria-hidden="true" width={size} height={size} viewBox="0 0 10 10" className="rh-pnl-arrow"><rect x="1" y="4.2" width="8" height="1.6" rx="0.8" fill="currentColor" /></svg>
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 10 10" className="rh-pnl-arrow">
      <path d={s > 0 ? 'M5 1.2 9.2 8.4H.8Z' : 'M5 8.8.8 1.6h8.4Z'} fill="currentColor" />
    </svg>
  )
}

export function Pct({ value, ratio, precision = 1, className, sign = 'auto' }: { value: string | null | undefined; ratio?: boolean; precision?: number; className?: string; sign?: SignMode }) {
  return (
    <span className={cx('num whitespace-nowrap', className)} title={value ?? undefined}>
      {formatPct(value, { ratio: ratio ?? false, precision, sign })}
    </span>
  )
}

// Token amounts ----------------------------------------------------------------------

export interface TokenMeta {
  symbol: string
  decimals: number
  name?: string
}
export type TokenResolver = (address: string) => TokenMeta | undefined

const KNOWN: Record<string, TokenMeta> = {
  '0x0000000000000000000000000000000000000000': { symbol: 'ETH', decimals: 18, name: 'Ether' },
  '0x0bd7d308f8e1639fab988df18a8011f41eacad73': { symbol: 'WETH', decimals: 18, name: 'Wrapped Ether' },
}
const TokenContext = createContext<TokenResolver>((a) => KNOWN[a.toLowerCase()])

/** Apps provide token metadata (symbols, decimals) seen in API responses. */
export const TokenResolverProvider = TokenContext.Provider

export function useToken(address: string | null | undefined): TokenMeta | undefined {
  const resolve = useContext(TokenContext)
  if (!address) return undefined
  return resolve(address.toLowerCase()) ?? KNOWN[address.toLowerCase()]
}

export function tokenLabel(address: string, meta: TokenMeta | undefined): string {
  return meta?.symbol ?? `${address.slice(0, 6)}…${address.slice(-4)}`
}

export function TokenSymbol({ address, className }: { address: string; className?: string }) {
  const meta = useToken(address)
  return (
    <span className={cx('font-mono text-[0.92em] font-semibold tracking-tight', className)} title={meta?.name ? `${meta.name} · ${address}` : address}>
      {tokenLabel(address, meta)}
    </span>
  )
}

/** Integer token units (wei) with the token's decimals and symbol. */
export function TokenAmount({
  amount,
  token,
  decimals,
  symbol,
  compact,
  className,
  showSymbol = true,
}: {
  amount: string
  token?: string
  decimals?: number | null
  symbol?: string | null
  compact?: boolean
  className?: string
  showSymbol?: boolean
}) {
  const meta = useToken(token)
  const d = decimals ?? meta?.decimals ?? 18
  const sym = symbol ?? meta?.symbol ?? (token ? tokenLabel(token, undefined) : '')
  return (
    <span className={cx('num whitespace-nowrap font-mono', className)} title={`${exactTokenAmount(amount, d)} ${sym}`.trim()}>
      {formatTokenAmount(amount, d, { compact: compact ?? false })}
      {showSymbol && sym && <span className="ml-1 font-semibold">{sym}</span>}
    </span>
  )
}
